import React, { useEffect } from 'react'
import { Badge, Stack } from 'react-bootstrap'
import { useTransactionStore } from '../api/transactions/store'
import {
    formatCurrency,
    formatTransactionDate,
    getAccountTypeColor,
    getAccountTypeLabel
} from '../api/transactions/data'
import CStack from './CStack'
import CAccordion from './CAccordion'
import Label from './Label'
import Loading from './Loading'
import { ReactComponent as ReloadIcon } from '../svgs/refresh.svg'

interface TransactionHistoryProps {
    clientId: string
    limit?: number
}

const TransactionHistory: React.FC<TransactionHistoryProps> = ({ clientId, limit = 50 }) => {
    const {
        transactions,
        loading,
        error,
        retrieveTransactions,
        clearTransactions
    } = useTransactionStore()

    useEffect(() => {
        if (clientId) {
            retrieveTransactions(clientId, limit)
        }

        return () => {
            // Clear transactions when component unmounts
            clearTransactions()
        }
    }, [clientId, limit])

    const handleRefresh = () => {
        clearTransactions()
        retrieveTransactions(clientId, limit)
    }

    if (loading && !transactions) {
        return <CStack header="Transaction History">
            <Loading expandHeight />
        </CStack>
    }

    if (error) {
        return (
            <CStack header="Transaction History">
                <Label color="red">{error}</Label>
            </CStack>
        )
    }

    return (
        <CStack
            header="Transaction History"
            actions={[{
                icon: <ReloadIcon width={20} height={20}/>,
                onClick: handleRefresh
            }]}
        >
            {!transactions || transactions.length === 0 ? (
                <Label>No transactions found</Label>
            ) : (
                <>
                    {transactions.map((transaction, index) => {
                        // Determine the amount and color to show in label
                        const amount = transaction.creditAmount || transaction.debitAmount || 0
                        const amountColor = transaction.creditAmount ? '#28a745' : '#dc3545'
                        const formattedAmount = transaction.creditAmount
                            ? `+${formatCurrency(amount)}`
                            : formatCurrency(amount)

                        // Create the label with account name and amount
                        const transactionLabel = (
                            <Stack direction="horizontal" gap={2}>
                                <span style={{ flex: 1 }}>{transaction.accountName || 'Account'}</span>
                                <span style={{ color: amountColor, fontWeight: '600' }}>
                                    {formattedAmount}
                                </span>
                            </Stack>
                        )

                        return (
                            <CAccordion
                                key={`${transaction.accountNumber}-${transaction.referenceNo}-${index}`}
                                label={transactionLabel as any}
                            >
                                <Stack gap={2} style={{ padding: '10px' }}>
                                    <Stack direction="horizontal" className="justify-content-between">
                                        <Label size="small" color="gray">Description:</Label>
                                        <Label size="small" textAlign="right">
                                            {transaction.transactionDescription}
                                        </Label>
                                    </Stack>

                                    <Stack direction="horizontal" className="justify-content-between">
                                        <Label size="small" color="gray">Date:</Label>
                                        <Label size="small" textAlign="right">
                                            {formatTransactionDate(transaction.transactionDate)}
                                        </Label>
                                    </Stack>

                                    <Stack direction="horizontal" className="justify-content-between">
                                        <Label size="small" color="gray">Account Number:</Label>
                                        <Label size="small" textAlign="right">
                                            {transaction.accountNumber}
                                        </Label>
                                    </Stack>

                                    <Stack direction="horizontal" className="justify-content-between">
                                        <Label size="small" color="gray">Account Type:</Label>
                                        <Badge bg={getAccountTypeColor(transaction.accountType)}>
                                            {getAccountTypeLabel(transaction.accountType)}
                                        </Badge>
                                    </Stack>

                                    {transaction.debitAmount && (
                                        <Stack direction="horizontal" className="justify-content-between">
                                            <Label size="small" color="gray">Debit:</Label>
                                            <Label size="small" color="red" textAlign="right">
                                                {formatCurrency(transaction.debitAmount)}
                                            </Label>
                                        </Stack>
                                    )}

                                    {transaction.creditAmount && (
                                        <Stack direction="horizontal" className="justify-content-between">
                                            <Label size="small" color="gray">Credit:</Label>
                                            <Label size="small" color="green" textAlign="right">
                                                {formatCurrency(transaction.creditAmount)}
                                            </Label>
                                        </Stack>
                                    )}

                                    {transaction.referenceNo && (
                                        <Stack direction="horizontal" className="justify-content-between">
                                            <Label size="small" color="gray">Reference:</Label>
                                            <Label size="small" textAlign="right">
                                                {transaction.referenceNo}
                                            </Label>
                                        </Stack>
                                    )}

                                    {transaction.remarks && (
                                        <Stack direction="horizontal" className="justify-content-between align-items-start">
                                            <Label size="small" color="gray" style={{ minWidth: '80px' }}>Remarks:</Label>
                                            <Label size="small" textAlign="right" style={{
                                                wordBreak: 'break-word',
                                                whiteSpace: 'pre-wrap',
                                                maxWidth: '60%',
                                                marginLeft: '10px'
                                            }}>
                                                {transaction.remarks}
                                            </Label>
                                        </Stack>
                                    )}

                                    {transaction.transferRemarks && (
                                        <Stack direction="horizontal" className="justify-content-between align-items-start">
                                            <Label size="small" color="gray" style={{ minWidth: '80px' }}>Transfer Remarks:</Label>
                                            <Label size="small" textAlign="right" style={{
                                                wordBreak: 'break-word',
                                                whiteSpace: 'pre-wrap',
                                                maxWidth: '60%',
                                                marginLeft: '10px'
                                            }}>
                                                {transaction.transferRemarks}
                                            </Label>
                                        </Stack>
                                    )}
                                </Stack>
                            </CAccordion>
                        )
                    })}

                    {transactions.length === limit && (
                        <div style={{ textAlign: 'center', marginTop: '10px' }}>
                            <Label size="small" color="gray">
                                {`Showing first ${limit} transactions`}
                            </Label>
                        </div>
                    )}
                </>
            )}
        </CStack>
    )
}

export default TransactionHistory